import React, { useState } from 'react';
import { SystemConfig } from '../../data/types';

interface IntegrationTabProps {
  config: SystemConfig;
  onSave: (updatedConfig: Partial<SystemConfig>) => void;
  saving: boolean;
}

const IntegrationTab: React.FC<IntegrationTabProps> = ({ config, onSave, saving }) => {
  const [lmsSettings, setLmsSettings] = useState({
    provider: config.integrationSettings.lmsConnection.provider,
    apiEndpoint: config.integrationSettings.lmsConnection.apiEndpoint,
    syncFrequency: config.integrationSettings.lmsConnection.syncFrequency
  });
  
  const [ssoSettings, setSsoSettings] = useState({
    provider: config.integrationSettings.ssoSettings.provider,
    clientId: config.integrationSettings.ssoSettings.clientId,
    enabled: config.integrationSettings.ssoSettings.enabled
  });
  
  const [csvSettings, setCsvSettings] = useState({
    automaticImport: config.integrationSettings.csvImport.automaticImport,
    importTime: config.integrationSettings.csvImport.importTime,
    notifyOnFailure: config.integrationSettings.csvImport.notifyOnFailure,
    notificationEmail: config.integrationSettings.csvImport.notificationEmail
  });
  
  const [activeSection, setActiveSection] = useState('lms'); // 'lms', 'sso', 'csv'
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');

  const handleSave = () => {
    const updatedConfig: Partial<SystemConfig> = {
      integrationSettings: {
        lmsConnection: {
          ...lmsSettings,
          lastSynced: config.integrationSettings.lmsConnection.lastSynced
        },
        ssoSettings: ssoSettings,
        csvImport: csvSettings
      }
    };
    
    onSave(updatedConfig);
  };

  const handleTestConnection = () => {
    setTestStatus('testing');
    
    // Simulate API call
    setTimeout(() => {
      // 80% chance of success
      if (Math.random() > 0.2) {
        setTestStatus('success');
      } else {
        setTestStatus('error');
      }
      
      // Reset after 3 seconds
      setTimeout(() => setTestStatus('idle'), 3000);
    }, 1500);
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">Integration Management</h2>
      <p className="text-gray-600 mb-6">
        Configure connections to external systems, including LMS integration, SSO authentication,
        and CSV data import settings.
      </p>
      
      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeSection === 'lms'
              ? 'border-b-2 border-primary text-primary'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setActiveSection('lms')}
        >
          LMS Connection
        </button>
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeSection === 'sso'
              ? 'border-b-2 border-primary text-primary'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setActiveSection('sso')}
        >
          SSO Authentication
        </button>
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeSection === 'csv'
              ? 'border-b-2 border-primary text-primary'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setActiveSection('csv')}
        >
          CSV Import
        </button>
      </div>
      
      {/* LMS Connection Settings */}
      {activeSection === 'lms' && (
        <div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">LMS Connection Status</h3>
                <p className="text-sm text-gray-500">
                  Last synced: {new Date(config.integrationSettings.lmsConnection.lastSynced).toLocaleString()}
                </p>
              </div>
              <button
                className={`px-3 py-1 rounded text-white ${
                  testStatus === 'testing' 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-primary-dark'
                }`}
                onClick={handleTestConnection}
                disabled={testStatus === 'testing'}
              >
                {testStatus === 'idle' && 'Test Connection'}
                {testStatus === 'testing' && 'Testing...'}
                {testStatus === 'success' && 'Connection Successful'}
                {testStatus === 'error' && 'Connection Failed'}
              </button>
            </div>
            
            {testStatus === 'success' && (
              <div className="p-2 bg-green-50 text-green-700 rounded">
                Successfully connected to LMS. All systems operational.
              </div>
            )}
            
            {testStatus === 'error' && (
              <div className="p-2 bg-red-50 text-red-700 rounded">
                Failed to connect to LMS. Please check your settings and try again.
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LMS Provider
              </label>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2"
                value={lmsSettings.provider}
                onChange={(e) => setLmsSettings({
                  ...lmsSettings,
                  provider: e.target.value
                })}
              >
                <option value="Canvas">Canvas</option>
                <option value="Blackboard">Blackboard</option>
                <option value="Moodle">Moodle</option>
                <option value="Schoology">Schoology</option>
                <option value="Google Classroom">Google Classroom</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sync Frequency
              </label>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2"
                value={lmsSettings.syncFrequency}
                onChange={(e) => setLmsSettings({
                  ...lmsSettings,
                  syncFrequency: e.target.value
                })}
              >
                <option value="daily">Daily</option>
                <option value="hourly">Hourly</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Endpoint
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={lmsSettings.apiEndpoint}
              onChange={(e) => setLmsSettings({
                ...lmsSettings,
                apiEndpoint: e.target.value
              })}
              placeholder="https://example.com/api/v1"
            />
            <p className="text-xs text-gray-500 mt-1">
              The base URL for the LMS API
            </p>
          </div>
          
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">API Credentials</h3>
            <p className="text-sm text-gray-500 mb-3">
              API keys and secrets are stored securely and not displayed here for security reasons.
            </p>
            <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
              Update API Credentials
            </button>
          </div>
        </div>
      )}
      
      {/* SSO Authentication Settings */}
      {activeSection === 'sso' && (
        <div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">SSO Status</h3>
                <p className="text-sm text-gray-500">
                  {ssoSettings.enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div className="flex items-center">
                <span className="mr-3 text-sm text-gray-500">
                  {ssoSettings.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={ssoSettings.enabled}
                    onChange={() => setSsoSettings({
                      ...ssoSettings,
                      enabled: !ssoSettings.enabled
                    })}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>
          
          {ssoSettings.enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SSO Provider
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    value={ssoSettings.provider}
                    onChange={(e) => setSsoSettings({
                      ...ssoSettings,
                      provider: e.target.value
                    })}
                  >
                    <option value="Google">Google</option>
                    <option value="Microsoft">Microsoft</option>
                    <option value="Clever">Clever</option>
                    <option value="ClassLink">ClassLink</option>
                    <option value="SAML">SAML</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    value={ssoSettings.clientId}
                    onChange={(e) => setSsoSettings({
                      ...ssoSettings,
                      clientId: e.target.value
                    })}
                    placeholder="client-id-123456"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-2">Client Secret</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Client secrets are stored securely and not displayed here for security reasons.
                </p>
                <button className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                  Update Client Secret
                </button>
              </div>
              
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-2">Callback URL</h3>
                <div className="flex items-center">
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-l px-3 py-2 bg-gray-50"
                    value="https://app.swunmath.com/auth/callback"
                    readOnly
                  />
                  <button className="bg-gray-100 border border-gray-300 border-l-0 rounded-r px-3 py-2 text-gray-600 hover:bg-gray-200">
                    Copy
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use this URL in your SSO provider configuration
                </p>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* CSV Import Settings */}
      {activeSection === 'csv' && (
        <div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Automatic CSV Import</h3>
                <p className="text-sm text-gray-500">
                  {csvSettings.automaticImport 
                    ? `Scheduled daily at ${csvSettings.importTime}` 
                    : 'Manual import only'}
                </p>
              </div>
              <div className="flex items-center">
                <span className="mr-3 text-sm text-gray-500">
                  {csvSettings.automaticImport ? 'Enabled' : 'Disabled'}
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={csvSettings.automaticImport}
                    onChange={() => setCsvSettings({
                      ...csvSettings,
                      automaticImport: !csvSettings.automaticImport
                    })}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>
          
          {csvSettings.automaticImport && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Import Time (24-hour format)
              </label>
              <input
                type="time"
                className="w-full md:w-1/3 border border-gray-300 rounded px-3 py-2"
                value={csvSettings.importTime}
                onChange={(e) => setCsvSettings({
                  ...csvSettings,
                  importTime: e.target.value
                })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Set the time when automatic imports should run daily
              </p>
            </div>
          )}
          
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="notifyOnFailure"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                checked={csvSettings.notifyOnFailure}
                onChange={() => setCsvSettings({
                  ...csvSettings,
                  notifyOnFailure: !csvSettings.notifyOnFailure
                })}
              />
              <label htmlFor="notifyOnFailure" className="ml-2 block text-sm font-medium text-gray-700">
                Notify on import failures
              </label>
            </div>
            
            {csvSettings.notifyOnFailure && (
              <div className="ml-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notification Email
                </label>
                <input
                  type="email"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  value={csvSettings.notificationEmail}
                  onChange={(e) => setCsvSettings({
                    ...csvSettings,
                    notificationEmail: e.target.value
                  })}
                  placeholder="admin@example.com"
                />
              </div>
            )}
          </div>
          
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">CSV Templates</h3>
            <p className="text-sm text-gray-500 mb-3">
              Download templates for CSV imports
            </p>
            <div className="space-y-2">
              <div>
                <a href="#" className="text-primary hover:underline text-sm">
                  Download Schools Template
                </a>
              </div>
              <div>
                <a href="#" className="text-primary hover:underline text-sm">
                  Download Teachers Template
                </a>
              </div>
              <div>
                <a href="#" className="text-primary hover:underline text-sm">
                  Download Students Template
                </a>
              </div>
              <div>
                <a href="#" className="text-primary hover:underline text-sm">
                  Download Standards Map Template
                </a>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">Manual Import</h3>
            <p className="text-sm text-gray-500 mb-3">
              Trigger a manual import now
            </p>
            <button className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">
              Go to Import Management
            </button>
          </div>
        </div>
      )}
      
      {/* Save Button */}
      <div className="flex justify-end mt-6">
        <button
          className={`px-4 py-2 rounded text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
          }`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Integration Settings'}
        </button>
      </div>
    </div>
  );
};

export default IntegrationTab;
